import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Plus, Trash2, AlertCircle, ShieldAlert, Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { tenantsApi, didApi, avatarsApi } from '../api/tenants.js';
import { useTenant } from '../components/TenantContext.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { PageHeader, SectionHeader } from '../components/ui/PageHeader.jsx';
import { Card, StatusDot } from '../components/ui/Card.jsx';
import { Input } from '../components/ui/Input.jsx';
import { Button } from '../components/ui/Button.jsx';
import { PbxModeSelector, CompatChecklistModal, TrunkSettingsCard, defaultTrunkConfig } from '../components/TrunkConfigSection.jsx';
import { overlayDismiss } from '../lib/overlayDismiss';

const SECTION_FIELDS = {
    basic: ['displayName', 'enabled', 'defaultTransferExt'],
    // transferMode 已移除獨立開關（2026-07-14）：由「轉接真人錄音」推導 — 錄音開=confbridge、關=REFER
    // （SIP Trunk 例外：trunk.transferMode 顯式指定，backend 優先讀它）
    // AckAt/AckBy = 錄音法律告知的確認稽核（誰、何時勾了「我已了解需宣告錄音」）
    lineBehavior: ['transferRingbackEnabled', 'aecEnabled', 'pickupSignal', 'transferRecordingEnabled', 'transferRecordingAckAt', 'transferRecordingAckBy', 'outboundCallerIdFromExt'],
    pbx: ['pbx'],
    ipPhones: ['ipPhones'],
    trunk: ['trunk'],
    outbound: ['outboundConfig'],
};

function deepEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}

export default function TenantEditPage() {
    const { t } = useTranslation('tenant');
    const { hostId } = useParams();
    const nav = useNavigate();
    const { reload: reloadTenants, tenants, loading: tenantsLoading, hasCS, accountRole, isSharedBusiness } = useTenant();
    const { user } = useAuth();
    const [recConsentOpen, setRecConsentOpen] = useState(false);   // 錄音法律告知確認視窗
    const [compatOpen, setCompatOpen] = useState(false);           // trunk 相容性檢查清單（ext→trunk 切換閘門）
    const [tenant, setTenant] = useState(null);
    const [savedTenant, setSavedTenant] = useState(null);   // baseline 用來判 dirty
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [registrations, setRegistrations] = useState([]);   // [{ext, status, expiresIn}]
    const [trunkStatus, setTrunkStatus] = useState(null);      // SIP Trunk 對接狀態 {authMode, status, rttMs|expiresIn}
    const [sectionStatus, setSectionStatus] = useState({});   // section → { saving, savedAt, applied, missing, hint }

    // URL 上的 hostId 不在 user 可訪問清單 → 跳回 list
    useEffect(() => {
        if (tenantsLoading) return;
        if (tenants.length === 0) return;
        if (!tenants.find((t) => t.hostId === hostId)) {
            console.warn(`[TenantEditPage] ${hostId} 不在 user 的 tenant 清單，redirect 到 /tenants`);
            nav('/tenants', { replace: true });
        }
    }, [hostId, tenants, tenantsLoading, nav]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await tenantsApi.get(hostId);
                if (!cancelled) {
                    setTenant(r.tenant);
                    setSavedTenant(r.tenant);
                }
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [hostId]);

    // 外部改動即時反映：SDX500「更新 AI 分機」等 provision 流程會從 PBX 端直接改 Firestore，
    // 頁面開著不會知道 → 回到分頁/視窗聚焦時 silent refetch。有未儲存編輯就不動（不可蓋掉使用者輸入）。
    const tenantRef = useRef(null);
    const savedTenantRef = useRef(null);
    tenantRef.current = tenant;
    savedTenantRef.current = savedTenant;
    useEffect(() => {
        let busy = false;
        const refreshIfClean = async () => {
            if (document.hidden || busy) return;
            const dirty = JSON.stringify(tenantRef.current) !== JSON.stringify(savedTenantRef.current);
            if (dirty) return;
            busy = true;
            try {
                const r = await tenantsApi.get(hostId);
                setTenant(r.tenant);
                setSavedTenant(r.tenant);
            } catch { /* 靜默失敗，維持現況 */ } finally { busy = false; }
        };
        document.addEventListener('visibilitychange', refreshIfClean);
        window.addEventListener('focus', refreshIfClean);
        return () => {
            document.removeEventListener('visibilitychange', refreshIfClean);
            window.removeEventListener('focus', refreshIfClean);
        };
    }, [hostId]);

    // PJSIP register 狀態：mount + 每 8 秒 poll
    useEffect(() => {
        let cancelled = false;
        async function poll() {
            try {
                const r = await tenantsApi.registrations(hostId);
                if (!cancelled) { setRegistrations(r.registrations || []); setTrunkStatus(r.trunk || null); }
            } catch {
                // sidecar 暫時不通就繼續，不掉 UI
            }
        }
        poll();
        const t = setInterval(poll, 8000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [hostId]);

    function update(patch) {
        setTenant((t) => ({ ...t, ...patch }));
    }
    function updatePbx(patch) {
        setTenant((t) => ({ ...t, pbx: { ...(t.pbx || {}), ...patch } }));
    }

    // ── SIP Trunk 對接模式 ──────────────────────────────────────────────────
    const isTrunk = tenant?.pbx?.mode === 'trunk';
    // 切換對接方式：預設僅 super_admin / dealer（商用帳號唯讀 —— 切錯代價高：conf 重產、
    // 進線對應失效、席次計費基礎改變，是我們跟客戶 PBX 團隊一起做的工程決定）。
    // 特例：客戶 PBX 不出 internet 只能走 trunk、且自己有 IT → 超管在 /admin 該租戶頁
    // 開通 trunkSelfServiceEnabled，該租戶的商用帳號就能自己切（仍要過相容性檢查清單）。
    const trunkSelfService = tenant?.trunkSelfServiceEnabled === true;
    const canSwitchMode = accountRole === 'super_admin'
        || accountRole === 'dealer'
        || (accountRole === 'business' && trunkSelfService);

    // ext→trunk：相容性檢查清單全過才執行（立即存檔，不走 section dirty 流程）
    async function confirmSwitchToTrunk(checks) {
        setCompatOpen(false);
        const newTrunk = {
            ...defaultTrunkConfig(),
            ...(tenant.trunk || {}),
            // REFER 沒跟 PBX 團隊確認 → 保守預設 confbridge（已存在的顯式設定不動）
            transferMode: tenant.trunk?.transferMode || (checks.referSupported ? 'refer' : 'confbridge'),
            compatChecked: {
                trunkSupported: !!checks.trunkSupported,
                internalExtAddressable: !!checks.internalExtAddressable,
                trunkToPstnTransfer: !!checks.trunkToPstnTransfer,
                callerIdSettable: !!checks.callerIdConfirmed,
                referSupported: !!checks.referSupported,
                checkedBy: user?.email || '',
                checkedAt: Date.now(),
            },
        };
        try {
            const r = await tenantsApi.upsert(hostId, { pbx: { ...(tenant.pbx || {}), mode: 'trunk' }, trunk: newTrunk });
            setTenant(r.tenant);
            setSavedTenant(r.tenant);
            reloadTenants();
        } catch (e) { setError(e); }
    }

    // trunk→ext：ipPhones 資料一直保留著（切換不刪），確認後直接還原
    async function switchToExtension() {
        if (!window.confirm(t('trunk.mode.backToExtConfirm'))) return;
        try {
            const r = await tenantsApi.upsert(hostId, { pbx: { ...(tenant.pbx || {}), mode: 'extension' } });
            setTenant(r.tenant);
            setSavedTenant(r.tenant);
            reloadTenants();
        } catch (e) { setError(e); }
    }

    function isSectionDirty(section) {
        if (!tenant || !savedTenant) return false;
        const fields = SECTION_FIELDS[section] || [];
        return fields.some((f) => !deepEqual(tenant[f], savedTenant[f]));
    }

    /**
     * 各 section 獨立儲存：只送該 section 自己的欄位
     * 後端 PUT 會 merge，保留 firestore 上其他欄位不動
     * applied=true 就會觸發 sidecar reload；applied=false 表示尚未填齊
     */
    async function saveSection(section) {
        if (!tenant) return;
        const fields = SECTION_FIELDS[section] || [];
        if (fields.length === 0) return;
        setSectionStatus((s) => ({ ...s, [section]: { saving: true } }));
        setError(null);
        try {
            const body = {};
            for (const f of fields) body[f] = tenant[f];
            const r = await tenantsApi.upsert(hostId, body);
            setTenant(r.tenant);
            setSavedTenant(r.tenant);
            setSectionStatus((s) => ({
                ...s,
                [section]: {
                    saving: false,
                    savedAt: new Date(),
                    applied: r.applied,
                    missing: r.missing,
                    hint: r.hint,
                },
            }));
            reloadTenants();

            // 改完 PBX / ipPhones 跟註冊狀態相關 → 加快 poll 看 PBX 端是否註冊成功
            // 外播設定改動不影響 PJSIP 註冊
            if (r.applied && (section === 'ipPhones' || section === 'pbx')) {
                fastPollRegistrations();
            }
        } catch (e) {
            setSectionStatus((s) => ({ ...s, [section]: { saving: false, error: e } }));
            setError(e);
        }
    }

    /**
     * 儲存後 2/4/6/8 秒各 poll 一次 registrations，讓 RegBadge 快點變綠
     * 不取代 8 秒 background poll，疊加加速感
     */
    const fastPollTimersRef = useRef([]);
    function fastPollRegistrations() {
        // 清掉之前還在跑的 timer
        fastPollTimersRef.current.forEach((id) => clearTimeout(id));
        fastPollTimersRef.current = [];
        for (const delay of [2000, 4000, 6000, 9000]) {
            const id = setTimeout(async () => {
                try {
                    const r = await tenantsApi.registrations(hostId);
                    setRegistrations(r.registrations || []);
                    setTrunkStatus(r.trunk || null);
                } catch {/* sidecar 暫時不通就跳過 */}
            }, delay);
            fastPollTimersRef.current.push(id);
        }
    }
    useEffect(() => () => fastPollTimersRef.current.forEach((id) => clearTimeout(id)), []);

    if (loading) {
        return <div className="text-ink-400 text-sm">{t('common:state.loading')}</div>;
    }
    if (error && !tenant) {
        return (
            <ErrorBanner error={error} onRetry={() => nav(0)} />
        );
    }
    if (!tenant) return null;

    return (
        <div className="max-w-4xl">
            <PageHeader
                eyebrow={`tenant · ${tenant.hostId}`}
                title={tenant.displayName || tenant.hostId}
                description={t('edit.description')}
                actions={
                    <Link to="/tenants">
                        <Button variant="ghost" size="md">
                            <ArrowLeft size={14} /> {t('common:actions.back')}
                        </Button>
                    </Link>
                }
            />
            {error && <ErrorBanner error={error} />}

            {/* 共用租戶的商用子帳號：租戶設定（PBX / 分機 / trunk / 外播）屬於上層帳號，這裡整頁唯讀。
                後端 PUT /tenants/:hostId 對 sharedBusiness 呼叫者一律 403，這裡只是 UX。 */}
            {isSharedBusiness && (
                <div className="mb-4 max-w-2xl flex items-start gap-2.5 bg-accent-50 border border-accent-300/60 rounded-lg px-3.5 py-3">
                    <ShieldAlert size={18} className="mt-0.5 shrink-0 text-accent-700" />
                    <div className="text-[12.5px] leading-relaxed text-ink-700">
                        <div className="font-medium text-accent-700">{t('edit.sharedReadonly.title')}</div>
                        <div className="mt-0.5">{t('edit.sharedReadonly.body')}</div>
                    </div>
                </div>
            )}

            <PbxWhitelistNotice />

            <fieldset disabled={isSharedBusiness} className={`min-w-0 border-0 p-0 m-0 ${isSharedBusiness ? 'opacity-90 [&_button]:cursor-not-allowed' : ''}`}>
            <div className="space-y-8">
                {/* Basic */}
                <Card>
                    <SectionHeader title={t('basic.title')} />
                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label={t('basic.displayName.label')}
                            value={tenant.displayName || ''}
                            onChange={(e) => update({ displayName: e.target.value })}
                            hint={t('basic.displayName.hint')}
                        />
                        <Input
                            label={t('basic.hostId.label')}
                            value={tenant.hostId}
                            disabled
                            mono
                            hint={t('basic.hostId.hint')}
                        />
                    </div>
                    <div className="mt-4 max-w-sm">
                        <Input
                            label={t('basic.defaultTransferExt.label')}
                            value={tenant.defaultTransferExt || ''}
                            onChange={(e) => update({ defaultTransferExt: e.target.value.trim() })}
                            placeholder={t('basic.defaultTransferExt.placeholder')}
                            mono
                            hint={t('basic.defaultTransferExt.hint')}
                        />
                    </div>
                    <SectionSaveBar
                        dirty={isSectionDirty('basic')}
                        status={sectionStatus.basic}
                        onSave={() => saveSection('basic')}
                    />
                </Card>

                {/* Line behavior — 這台客戶 PBX 的線路特性開關（per-trunk，預設關，遇症狀才開）*/}
                <Card>
                    <SectionHeader title={t('lineBehavior.title')} description={t('lineBehavior.description')} />
                    <div className="space-y-5">
                        <div>
                            <Toggle
                                label={t('basic.ringback.label')}
                                value={tenant.transferRingbackEnabled === true}
                                onChange={(v) => update({ transferRingbackEnabled: v })}
                            />
                        </div>
                        <div>
                            <Toggle
                                label={t('basic.aec.label')}
                                value={tenant.aecEnabled !== false}   /* 0908 起預設開：沒設 = 開，明確 false 才關 */
                                onChange={(v) => update({ aecEnabled: v })}
                            />
                            <p className="mt-1.5 text-xs text-ink-500 max-w-xl leading-relaxed">{t('basic.aec.hint')}</p>
                        </div>
                        <div>
                            <Toggle
                                label={t('lineBehavior.pickupSignal.label')}
                                value={tenant.pickupSignal === 'sip' || (isTrunk && !tenant.pickupSignal)}
                                onChange={(v) => update({ pickupSignal: v ? 'sip' : 'audio' })}
                            />
                            <p className="mt-1.5 text-xs text-ink-500 max-w-xl leading-relaxed">{isTrunk ? t('lineBehavior.pickupSignal.hintTrunk') : t('lineBehavior.pickupSignal.hint')}</p>
                        </div>
                        <div>
                            <Toggle
                                label={t('lineBehavior.transferRecording.label')}
                                value={tenant.transferRecordingEnabled === true}
                                onChange={(v) => {
                                    if (v) setRecConsentOpen(true);   // 開啟前必須過法律告知確認
                                    else update({ transferRecordingEnabled: false });
                                }}
                                disabled={!hasCS}
                            />
                            {tenant.transferRecordingEnabled === true ? (
                                /* 已啟用 → 常駐醒目告知義務警示（不是灰色小字） */
                                <div className="mt-2 max-w-xl flex items-start gap-2.5 bg-warn-100 border-2 border-[#d9a93f] rounded-lg px-3.5 py-3">
                                    <ShieldAlert size={18} className="mt-0.5 shrink-0 text-[#8a5e16]" />
                                    <div className="text-[12.5px] leading-relaxed text-[#7a5512]">
                                        <b>{t('recConsent.activeTitle')}</b>
                                        <div className="mt-0.5">{t('recConsent.activeBody')}</div>
                                        {tenant.transferRecordingAckAt && (
                                            <div className="mt-1 font-mono text-[10.5px] opacity-70">
                                                {t('recConsent.ackedAt')} {String(tenant.transferRecordingAckAt).slice(0, 19).replace('T', ' ')} · {tenant.transferRecordingAckBy || ''}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <p className="mt-1.5 text-xs text-ink-500 max-w-xl leading-relaxed">
                                    {!hasCS ? t('lineBehavior.transferRecording.csOnly')
                                        : isTrunk ? t('lineBehavior.transferRecording.hintTrunk')
                                        : t('lineBehavior.transferRecording.hint')}
                                </p>
                            )}
                        </div>
                        {/* 分機模式才有意義：SIP Trunk 的顯號由 Trunk 設定決定 */}
                        {!isTrunk && (
                            <div>
                                <Toggle
                                    label={t('lineBehavior.outboundCallerIdFromExt.label')}
                                    value={tenant.outboundCallerIdFromExt === true}
                                    onChange={(v) => update({ outboundCallerIdFromExt: v })}
                                />
                                <p className="mt-1.5 text-xs text-ink-500 max-w-xl leading-relaxed">{t('lineBehavior.outboundCallerIdFromExt.hint')}</p>
                            </div>
                        )}
                    </div>
                    <SectionSaveBar
                        dirty={isSectionDirty('lineBehavior')}
                        status={sectionStatus.lineBehavior}
                        onSave={() => saveSection('lineBehavior')}
                    />
                </Card>

                {/* PBX */}
                <Card>
                    <SectionHeader
                        title={t('pbx.title')}
                        description={t('pbx.description')}
                    >
                        <PbxConnectionBadge
                            hostId={hostId}
                            pbxConfigured={!!tenant.pbx?.publicIp}
                            ipPhones={tenant.ipPhones || []}
                            registrations={registrations}
                            isTrunk={isTrunk}
                            trunkStatus={trunkStatus}
                        />
                    </SectionHeader>
                    <PbxModeSelector
                        mode={tenant.pbx?.mode || 'extension'}
                        canEdit={canSwitchMode}
                        grantedHint={accountRole === 'business' && trunkSelfService}
                        onSwitchToTrunk={() => setCompatOpen(true)}
                        onSwitchToExtension={switchToExtension}
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label={t('pbx.publicIp.label')}
                            value={tenant.pbx?.publicIp || ''}
                            onChange={(e) => updatePbx({ publicIp: e.target.value })}
                            placeholder={t('pbx.publicIp.placeholder')}
                            mono
                            hint={t('pbx.publicIp.hint')}
                        />
                        <Input
                            label={t('pbx.publicSipPort.label')}
                            value={tenant.pbx?.publicSipPort || ''}
                            onChange={(e) => updatePbx({ publicSipPort: Number(e.target.value) || 5060 })}
                            placeholder="5060"
                            mono
                            hint={t('pbx.publicSipPort.hint')}
                        />
                        <Input
                            label={t('pbx.lanIp.label')}
                            value={tenant.pbx?.lanIp || ''}
                            onChange={(e) => updatePbx({ lanIp: e.target.value })}
                            placeholder={t('pbx.lanIp.placeholder')}
                            mono
                            hint={t('pbx.lanIp.hint')}
                        />
                        <Input
                            label={t('pbx.lanSipPort.label')}
                            value={tenant.pbx?.lanSipPort || ''}
                            onChange={(e) => updatePbx({ lanSipPort: Number(e.target.value) || 5062 })}
                            placeholder="5062"
                            mono
                        />
                        <Input
                            label={isTrunk ? t('trunk.port.label') : t('pbx.ipPhoneRegisterPort.label')}
                            value={tenant.pbx?.ipPhoneRegisterPort || ''}
                            onChange={(e) =>
                                updatePbx({ ipPhoneRegisterPort: Number(e.target.value) || 5060 })
                            }
                            placeholder={t('pbx.ipPhoneRegisterPort.placeholder')}
                            mono
                            hint={isTrunk ? t('trunk.port.hint') : t('pbx.ipPhoneRegisterPort.hint')}
                        />
                        <Input
                            label={t('pbx.model.label')}
                            value={tenant.pbx?.model || ''}
                            onChange={(e) => updatePbx({ model: e.target.value })}
                            placeholder={t('pbx.model.placeholder')}
                            hint={t('pbx.model.hint')}
                        />
                        <Input
                            label={t('pbx.sipDomain.label')}
                            value={tenant.pbx?.sipDomain || ''}
                            onChange={(e) => updatePbx({ sipDomain: e.target.value.trim() })}
                            placeholder={t('pbx.sipDomain.placeholder')}
                            mono
                            hint={t('pbx.sipDomain.hint')}
                        />
                        <div>
                            <label className="block text-[10px] uppercase tracking-wider font-mono text-ink-400 mb-1">{t('pbx.sourcePort.label')}</label>
                            <select
                                value={tenant.pbx?.sourcePort || 5060}
                                onChange={(e) => updatePbx({ sourcePort: Number(e.target.value) })}
                                className="w-full px-2 py-1.5 text-sm font-mono bg-white border border-cream-200 rounded focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                            >
                                {[5060, 5062, 5064, 5066, 5068].map((p) => (
                                    <option key={p} value={p}>{p}{p === 5060 ? ` (${t('pbx.sourcePort.default')})` : ''}</option>
                                ))}
                            </select>
                            <p className="mt-1 text-[11px] text-ink-400">{t('pbx.sourcePort.hint')}</p>
                        </div>
                    </div>
                    <SectionSaveBar
                        dirty={isSectionDirty('pbx')}
                        status={sectionStatus.pbx}
                        onSave={() => saveSection('pbx')}
                        applyHint={t('pbx.applyHint')}
                    />
                </Card>

                {/* Trunk 連線設定（trunk 模式）↔ IP Phones 清單（分機模式）——互斥顯示 */}
                {isTrunk && (
                    <TrunkSettingsCard
                        hostId={hostId}
                        trunk={tenant.trunk}
                        onChange={(tr) => update({ trunk: tr })}
                        dirty={isSectionDirty('trunk')}
                        status={sectionStatus.trunk}
                        onSave={() => saveSection('trunk')}
                        platformIp={REGION_PLATFORM_IPS[tenant.region] || PLATFORM_PUBLIC_IP}
                        recordingOn={!!tenant.transferRecordingEnabled}
                    />
                )}
                {!isTrunk && (
                <CollapsibleCard
                    title={t('ipPhones.title')}
                    description={t('ipPhones.description')}
                    summary={t('ipPhones.summary', { n: (tenant.ipPhones || []).length })}
                    headerExtra={
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                                update({
                                    ipPhones: [
                                        ...(tenant.ipPhones || []),
                                        { ext: '', password: '' },
                                    ],
                                })
                            }
                        >
                            <Plus size={14} /> {t('ipPhones.add')}
                        </Button>
                    }
                >
                    <IpPhonesEditor
                        phones={tenant.ipPhones || []}
                        hostId={tenant.hostId}
                        registrations={registrations}
                        onChange={(phones) => update({ ipPhones: phones })}
                    />
                    <SectionSaveBar
                        dirty={isSectionDirty('ipPhones')}
                        status={sectionStatus.ipPhones}
                        onSave={() => saveSection('ipPhones')}
                        applyHint={t('ipPhones.applyHint')}
                    />
                </CollapsibleCard>
                )}

                {/* Outbound 外播設定 — 可收合 */}
                <CollapsibleCard
                    title={t('outbound.title')}
                    description={t('outbound.description')}
                    summary={t('outbound.summary', { prefix: (tenant.outboundConfig?.dialPrefix || tenant.outboundDialPrefix || t('outbound.none')) })}
                >
                    <OutboundConfigInput
                        value={tenant.outboundConfig || {}}
                        ipPhonesCount={(tenant.ipPhones || []).length}
                        trunkChannels={isTrunk ? Number(tenant.trunk?.channels) || 0 : 0}
                        onChange={(c) => update({ outboundConfig: c })}
                    />
                    <SectionSaveBar
                        dirty={isSectionDirty('outbound')}
                        status={sectionStatus.outbound}
                        onSave={() => saveSection('outbound')}
                    />
                </CollapsibleCard>

                {/* Inbound DID → Avatar mapping — 可收合 */}
                <CollapsibleCard
                    title={t('inbound.title')}
                    description={t('inbound.description')}
                >
                    <DidMappingTable
                        hostId={hostId}
                        outboundPrefixes={tenant.outboundDialPrefixes || []}
                        internalExts={isTrunk
                            // trunk 模式沒有「我方分機」概念：doc 上殘留的 ipPhones（切換前的分機）不能拿來過濾，
                            // 否則同號碼的 DID 會被當內線藏起來、永遠顯示在「尚未存」（0828 宏華 CTI02 實案：綁定成功 UI 卻說沒存）
                            ? []
                            : (tenant.ipPhones || []).map((p) => p.ext)}
                        trunkMode={isTrunk}
                        trunkDids={isTrunk ? (tenant.trunk?.dids || []) : []}
                    />
                </CollapsibleCard>

                {/* 內線路由：內線分機轉接到我們的 ext 時用哪個 AI — 分機模式限定（trunk 沒有我方分機概念）*/}
                {!isTrunk && (
                <CollapsibleCard
                    title={t('internal.title')}
                    description={t('internal.description')}
                    summary={t('internal.summary', { n: (tenant.ipPhones || []).length })}
                >
                    <InternalExtMappingTable
                        hostId={hostId}
                        ipPhones={tenant.ipPhones || []}
                    />
                </CollapsibleCard>
                )}

                {/* Generated endpoints preview */}
                <Card className="bg-cream-100 border-cream-300">
                    <SectionHeader
                        title={t('preview.title')}
                        description={t('preview.description')}
                    />
                    <div className="space-y-2 text-xs">
                        {isTrunk ? (
                            <div className="flex items-center gap-3 text-ink-700">
                                <span className="px-2 py-0.5 bg-white rounded border border-cream-200">
                                    <strong className="font-mono">{tenant.hostId}-trunk</strong>
                                </span>
                                <span className="text-ink-400">→</span>
                                <span className="font-mono">
                                    {tenant.pbx?.publicIp || '?'}:{tenant.pbx?.ipPhoneRegisterPort || '?'}
                                </span>
                                <span className="text-ink-400">
                                    {t('trunk.preview.channels', { n: Number(tenant.trunk?.channels) || '?' })}
                                </span>
                            </div>
                        ) : (
                            <>
                        {(tenant.ipPhones || []).map((p) => (
                            <div key={p.ext} className="flex items-center gap-3 text-ink-700">
                                <span className="px-2 py-0.5 bg-white rounded border border-cream-200">
                                    {t('preview.extLabel')} <strong className="font-mono">{p.ext}</strong>
                                </span>
                                <span className="text-ink-400">→</span>
                                <span className="font-mono">
                                    {p.ext}@{tenant.pbx?.publicIp || '?'}:{tenant.pbx?.ipPhoneRegisterPort || '?'}
                                </span>
                            </div>
                        ))}
                        {(!tenant.ipPhones || tenant.ipPhones.length === 0) && (
                            <div className="text-ink-400 italic">{t('preview.empty')}</div>
                        )}
                            </>
                        )}
                    </div>
                </Card>
            </div>
            </fieldset>

            {/* trunk 相容性檢查清單（ext→trunk 切換閘門） */}
            {compatOpen && (
                <CompatChecklistModal
                    tenant={tenant}
                    onCancel={() => setCompatOpen(false)}
                    onConfirm={confirmSwitchToTrunk}
                />
            )}

            {/* 錄音法律告知確認（開啟「轉接真人錄音」前必經） */}
            {recConsentOpen && (
                <RecordingConsentModal
                    onCancel={() => setRecConsentOpen(false)}
                    onConfirm={() => {
                        update({
                            transferRecordingEnabled: true,
                            transferRecordingAckAt: new Date().toISOString(),
                            transferRecordingAckBy: user?.email || '',
                        });
                        setRecConsentOpen(false);
                    }}
                />
            )}
        </div>
    );
}

/**
 * 錄音法律告知確認視窗 — 東訊要求醒目宣告，確保盡到告知義務（法律責任歸屬清楚）。
 * 必須勾選「我已了解」才能啟用；確認時間與操作者記進 tenant（AckAt/AckBy）留稽核證據。
 */
function RecordingConsentModal({ onCancel, onConfirm }) {
    const { t } = useTranslation('tenant');
    const [acked, setAcked] = useState(false);
    return (
        <>
            <div className="fixed inset-0 bg-ink-900/40 z-40" {...overlayDismiss(onCancel)} />
            <div className="fixed inset-0 z-50 grid place-items-center p-4 pointer-events-none">
                <div className="pointer-events-auto w-full max-w-lg bg-white rounded-2xl shadow-2xl border-2 border-[#d9a93f] overflow-hidden">
                    <div className="bg-warn-100 px-6 py-4 flex items-center gap-3 border-b border-[#e7cf9a]">
                        <ShieldAlert size={22} className="text-[#8a5e16] shrink-0" />
                        <h3 className="heading-display text-lg font-semibold text-[#7a5512]">{t('recConsent.title')}</h3>
                    </div>
                    <div className="px-6 py-5 space-y-3 text-[13.5px] text-ink-700 leading-relaxed">
                        <p><b>{t('recConsent.p1')}</b></p>
                        <p>{t('recConsent.p2')}</p>
                        <p className="text-danger-500">{t('recConsent.p3')}</p>
                        <label className="flex items-start gap-2.5 mt-2 p-3 rounded-lg bg-cream-50 border border-cream-200 cursor-pointer select-none">
                            <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} className="size-4 mt-0.5 accent-accent-500 shrink-0" />
                            <span className="text-[13px] text-ink-900 font-medium">{t('recConsent.checkbox')}</span>
                        </label>
                    </div>
                    <div className="px-6 py-4 bg-cream-50 border-t border-cream-200 flex justify-end gap-2.5">
                        <Button variant="outline" onClick={onCancel}>{t('recConsent.cancel')}</Button>
                        <Button variant="accent" onClick={onConfirm} disabled={!acked}>{t('recConsent.confirm')}</Button>
                    </div>
                </div>
            </div>
        </>
    );
}

function IpPhonesEditor({ phones, hostId, registrations = [], onChange }) {
    const { t } = useTranslation('tenant');
    function update(idx, patch) {
        onChange(phones.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
    }
    function remove(idx) {
        onChange(phones.filter((_, i) => i !== idx));
    }
    function setDirectPassword(idx, value) {
        // 寫進 password 欄位；同時清掉 legacy passwordEnvVar（避免兩者並存衝突）
        onChange(
            phones.map((p, i) =>
                i === idx ? { ...p, password: value, passwordEnvVar: undefined } : p
            )
        );
    }

    const regByExt = new Map(registrations.map((r) => [r.ext, r]));

    if (phones.length === 0) {
        return (
            <div className="text-sm text-ink-400 italic text-center py-6 border border-dashed border-cream-300 rounded-md">
                {t('ipPhones.editor.empty')}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="grid grid-cols-[1fr_1.3fr_2fr_auto_auto] gap-3 px-1 font-mono text-[10px] uppercase tracking-wider text-ink-400">
                <span>ext</span>
                <span>{t('ipPhones.editor.account')}</span>
                <span>{t('ipPhones.editor.password')}</span>
                <span>{t('ipPhones.editor.registerStatus')}</span>
                <span></span>
            </div>
            {phones.map((p, i) => (
                <PhoneRow
                    key={i}
                    phone={p}
                    index={i}
                    update={update}
                    setDirectPassword={setDirectPassword}
                    remove={remove}
                    registration={regByExt.get(String(p.ext))}
                />
            ))}
        </div>
    );
}

function RegBadge({ registration }) {
    const { t } = useTranslation('tenant');
    if (!registration) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono text-ink-400 bg-cream-100">
                <span className="size-1.5 rounded-full bg-ink-300" />
                {t('reg.notApplied')}
            </span>
        );
    }
    const isOk = registration.status === 'Registered';
    const isTrying = registration.status === 'Trying' || registration.status === 'Sent';
    const color = isOk
        ? 'text-success-500 bg-success-100'
        : isTrying
        ? 'text-warn-500 bg-warn-100'
        : 'text-danger-500 bg-danger-100';
    const dot = isOk ? 'bg-success-500' : isTrying ? 'bg-warn-500' : 'bg-danger-500';
    return (
        <span className={['inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium tnum', color].join(' ')}>
            <span className={['size-1.5 rounded-full', dot].join(' ')} />
            {registration.status}
            {registration.expiresIn != null && (
                <span className="text-ink-400 font-mono">· {registration.expiresIn}s</span>
            )}
        </span>
    );
}

function PhoneRow({ phone, index, update, setDirectPassword, remove, registration }) {
    const { t } = useTranslation('tenant');
    // legacy mode: 沒 password 但有 passwordEnvVar → 顯示 env 名 + 「改用直接輸入密碼」按鈕
    const isLegacy = !phone.password && phone.passwordEnvVar;
    const [reveal, setReveal] = useState(false);
    const [editingLegacy, setEditingLegacy] = useState(false);

    return (
        <div className="grid grid-cols-[1fr_1.3fr_2fr_auto_auto] gap-3 items-center">
            <Input
                value={phone.ext}
                onChange={(e) => update(index, { ext: e.target.value.replace(/[^0-9]/g, '') })}
                placeholder="327"
                mono
            />
            {/* 帳號：雲端總機的「話機帳號」可 ≠ 分機號（英數+符號）；留空 = 跟分機號相同 */}
            <Input
                value={phone.authUser || ''}
                onChange={(e) => update(index, { authUser: e.target.value })}
                placeholder={phone.ext ? t('ipPhones.row.accountPlaceholder', { ext: phone.ext }) : t('ipPhones.editor.account')}
                mono
            />
            {isLegacy && !editingLegacy ? (
                <div>
                    <div className="px-3 py-2 bg-cream-100 border border-cream-300 rounded-md text-xs text-ink-500 font-mono flex items-center justify-between">
                        <span>{t('ipPhones.row.fromEnv', { envVar: phone.passwordEnvVar })}</span>
                        <button
                            type="button"
                            onClick={() => setEditingLegacy(true)}
                            className="text-accent-700 hover:underline"
                        >
                            {t('ipPhones.row.switchToDirect')}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="relative">
                    <Input
                        type={reveal ? 'text' : 'password'}
                        value={phone.password || ''}
                        onChange={(e) => setDirectPassword(index, e.target.value)}
                        placeholder={t('ipPhones.row.passwordPlaceholder')}
                        mono
                        className="pr-16"
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        onClick={() => setReveal((r) => !r)}
                        className="absolute right-3 top-2 text-xs text-ink-400 hover:text-ink-700 font-mono"
                    >
                        {reveal ? t('ipPhones.row.hide') : t('ipPhones.row.show')}
                    </button>
                </div>
            )}
            <RegBadge registration={registration} />
            <Button variant="ghost" size="sm" onClick={() => remove(index)}>
                <Trash2 size={14} className="text-danger-500" />
            </Button>
        </div>
    );
}

function DialPrefixesInput({ prefixes, onChange }) {
    const { t } = useTranslation('tenant');
    function remove(idx) {
        onChange(prefixes.filter((_, i) => i !== idx));
    }
    function add(val) {
        const v = val.trim();
        if (!v || prefixes.includes(v)) return;
        onChange([...prefixes, v]);
    }
    return (
        <div className="flex flex-wrap items-center gap-2">
            {prefixes.map((p, i) => (
                <span
                    key={p + i}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cream-100 border border-cream-300 rounded-md font-mono text-xs"
                >
                    {p}
                    <button onClick={() => remove(i)} className="text-ink-400 hover:text-danger-500">
                        ×
                    </button>
                </span>
            ))}
            <input
                placeholder={t('dialPrefixes.addPlaceholder')}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        add(e.currentTarget.value);
                        e.currentTarget.value = '';
                    }
                }}
                className="px-2.5 py-1 bg-white border border-cream-300 rounded-md font-mono text-xs focus:outline-none focus:border-accent-500"
            />
        </div>
    );
}

function OutboundConfigInput({ value, ipPhonesCount, trunkChannels = 0, onChange }) {
    const { t } = useTranslation('tenant');
    const dialPrefix = typeof value.dialPrefix === 'string' ? value.dialPrefix : '';
    const maxTrunks = Number.isFinite(Number(value.maxTrunks)) && Number(value.maxTrunks) > 0
        ? Number(value.maxTrunks)
        : '';
    // trunk 模式：預設併發上限 = trunk.channels（不是分機數）
    const capBase = trunkChannels > 0 ? trunkChannels : ipPhonesCount;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-xs text-ink-500 mb-1">{t('outboundConfig.prefix.label')}</label>
                <input
                    type="text"
                    value={dialPrefix}
                    onChange={(e) => onChange({ ...value, dialPrefix: e.target.value.trim() })}
                    placeholder={t('outboundConfig.prefix.placeholder')}
                    className="w-full px-3 py-2 bg-white border border-cream-300 rounded-md font-mono text-sm focus:outline-none focus:border-accent-500"
                />
                <p className="text-xs text-ink-400 mt-1">
                    {t('outboundConfig.prefix.hint')}
                </p>
            </div>
            <div>
                <label className="block text-xs text-ink-500 mb-1">{t('outboundConfig.maxLines.label')}</label>
                <input
                    type="number"
                    min="1"
                    value={maxTrunks}
                    onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        onChange({ ...value, maxTrunks: Number.isFinite(n) && n > 0 ? n : null });
                    }}
                    placeholder={capBase ? t('outboundConfig.maxLines.placeholderDefault', { n: capBase }) : t('outboundConfig.maxLines.placeholderRequired')}
                    className="w-full px-3 py-2 bg-white border border-cream-300 rounded-md text-sm focus:outline-none focus:border-accent-500"
                />
                <p className="text-xs text-ink-400 mt-1">
                    {t('outboundConfig.maxLines.hint')}
                </p>
            </div>
        </div>
    );
}

function Toggle({ label, value, onChange, disabled }) {
    return (
        <label className={['flex items-center gap-2.5', disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'].join(' ')}>
            <span
                className={[
                    'relative inline-flex w-9 h-5 rounded-full transition',
                    value ? 'bg-accent-500' : 'bg-cream-300',
                ].join(' ')}
                onClick={() => { if (!disabled) onChange(!value); }}
            >
                <span
                    className={[
                        'absolute top-0.5 inline-block size-4 bg-white rounded-full transition shadow-sm',
                        value ? 'left-4' : 'left-0.5',
                    ].join(' ')}
                />
            </span>
            <span className="text-sm text-ink-700">{label}</span>
        </label>
    );
}

function translateMissing(field, t) {
    const keyMap = {
        pbx: 'missing.pbx',
        'pbx.publicIp': 'missing.pbxPublicIp',
        'pbx.lanIp': 'missing.pbxLanIp',
        'pbx.ipPhoneRegisterPort': 'missing.pbxRegisterPort',
        ipPhones: 'missing.ipPhones',
        'ipPhones[].ext': 'missing.ipPhonesExt',
    };
    if (keyMap[field]) return t(keyMap[field]);
    if (field.startsWith('ipPhones[ext=')) {
        const ext = field.match(/ext=([^\]]+)/)?.[1];
        return t('missing.extPassword', { ext });
    }
    return field;
}

function ErrorBanner({ error, onRetry }) {
    const { t } = useTranslation('tenant');
    return (
        <div className="mb-6 px-4 py-3 bg-danger-100/60 border border-danger-500/30 text-danger-500 text-sm rounded-md flex items-center gap-3">
            <AlertCircle size={16} />
            <div className="flex-1">{error?.message || String(error)}</div>
            {onRetry && (
                <Button size="sm" variant="ghost" onClick={onRetry}>
                    {t('common:actions.retry')}
                </Button>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════
// PBX 白名單提醒 — 客戶 IT 必須把此 IP 加進 PBX 白名單，否則 SIP 註冊會被擋
// ════════════════════════════════════════════════════════════
const PLATFORM_PUBLIC_IP = '43.213.126.202';
// 各區我方對外 EIP —— trunk 對接資訊要顯示「服務該 tenant 那區」的 IP（客戶把 trunk 指過來的目標）
const REGION_PLATFORM_IPS = { tw: '43.213.126.202', th: '43.209.126.4' };

function PbxWhitelistNotice() {
    const { t } = useTranslation('tenant');
    const [copied, setCopied] = useState(false);
    async function copy() {
        try {
            await navigator.clipboard.writeText(PLATFORM_PUBLIC_IP);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {}
    }
    return (
        <div className="mb-6 px-4 py-3 bg-success-100 border border-success-500/40 rounded-md flex items-start gap-3">
            <ShieldAlert size={18} className="text-success-500 shrink-0 mt-0.5" strokeWidth={1.7} />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-success-500">
                    {t('whitelist.title')}
                </div>
                <div className="text-xs text-ink-500 mt-1 leading-relaxed">
                    {t('whitelist.description')}
                </div>
                <div className="mt-2 inline-flex items-center gap-2">
                    <code className="font-mono text-base font-semibold text-ink-900 bg-white px-3 py-1.5 rounded border border-cream-300 select-all">
                        {PLATFORM_PUBLIC_IP}
                    </code>
                    <Button size="sm" variant="ghost" onClick={copy}>
                        {copied ? <><Check size={12} className="text-success-500" /> {t('whitelist.copied')}</> : <><Copy size={12} /> {t('whitelist.copy')}</>}
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════
// PBX connection badge — 用 registrations 推導：有任一 ext 註冊成功 = PBX 通
// ════════════════════════════════════════════════════════════
function PbxConnectionBadge({ hostId, pbxConfigured, ipPhones, registrations, isTrunk = false, trunkStatus = null }) {
    const { t } = useTranslation('tenant');
    const [probing, setProbing] = useState(false);
    const [probe, setProbe] = useState(null);   // { reachable, rttMs, response?, error? }

    async function runProbe() {
        if (!pbxConfigured) return;
        setProbing(true);
        try {
            const r = await tenantsApi.probePbx(hostId);
            setProbe(r);
        } catch (e) {
            setProbe({ reachable: false, error: e.message });
        } finally {
            setProbing(false);
        }
    }

    // mount + 每 30 秒自動 probe（PBX 設定有填才跑）
    useEffect(() => {
        if (!pbxConfigured) return;
        runProbe();
        const t = setInterval(runProbe, 30000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId, pbxConfigured]);

    const refreshBtn = (
        <button
            onClick={runProbe}
            disabled={probing || !pbxConfigured}
            className="text-[10px] mono-caption text-ink-400 hover:text-accent-700 transition disabled:opacity-50"
            title={t('probe.retest')}
        >
            {probing ? t('probe.testing') : t('probe.retestShort')}
        </button>
    );

    // PBX 還沒填
    if (!pbxConfigured) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cream-100 text-ink-400 text-xs rounded-full border border-cream-200">
                <span className="size-1.5 rounded-full bg-ink-300" />
                {t('probe.fillFirst')}
            </span>
        );
    }

    // ── SIP Trunk 模式：對接成功與否看 trunk 狀態（IP 型=qualify Avail、註冊型=Registered），
    //    不看分機數（trunk 沒有分機）。probe RTT 當補充資訊放 title。
    if (isTrunk) {
        const ok = trunkStatus && (trunkStatus.status === 'Avail' || trunkStatus.status === 'Registered');
        const cls = ok
            ? 'bg-success-100/60 text-success-500 border-success-500/30'
            : trunkStatus
                ? 'bg-danger-100/60 text-danger-500 border-danger-500/30'
                : 'bg-cream-100 text-ink-400 border-cream-200';
        const dot = ok ? 'bg-success-500' : trunkStatus ? 'bg-danger-500' : 'bg-ink-300 animate-pulse';
        const detail = trunkStatus
            ? (trunkStatus.rttMs != null ? `${trunkStatus.rttMs}ms`
                : trunkStatus.expiresIn != null ? `exp ${trunkStatus.expiresIn}s` : trunkStatus.status)
            : '';
        const label = !trunkStatus
            ? t('probe.trunkChecking')
            : ok
                ? t('probe.trunkOk', { detail })
                : t('probe.trunkBad', { status: trunkStatus.status });
        return (
            <div className="flex items-center gap-2">
                <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border ${cls}`}
                    title={probe ? `SIP OPTIONS ${probe.reachable ? 'ok' : 'fail'}${probe.rttMs ? ` · ${probe.rttMs}ms` : ''}` : ''}
                >
                    <span className={`size-1.5 rounded-full ${dot}`} />
                    {label}
                </span>
                {refreshBtn}
            </div>
        );
    }

    // 還沒拿到 probe 結果
    if (!probe) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cream-100 text-ink-400 text-xs rounded-full border border-cream-200">
                <span className="size-1.5 rounded-full bg-ink-300 animate-pulse" />
                {t('probe.testingConnection')}
            </span>
        );
    }

    const total = ipPhones?.length || 0;
    const registeredCount = (registrations || []).filter((r) => r.status === 'Registered').length;

    if (!probe.reachable) {
        // 有 ext registered = PBX 一定通的（probe 不認證可能被擋，但 register 過了）
        if (registeredCount > 0) {
            return (
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-success-100/60 text-success-500 text-xs rounded-full border border-success-500/30"
                        title={t('probe.okNoResponseTitle', { registered: registeredCount, total })}>
                        <span className="size-1.5 rounded-full bg-success-500" />
                        {t('probe.okRegistered', { registered: registeredCount, total })}
                    </span>
                    {refreshBtn}
                </div>
            );
        }
        return (
            <div className="flex items-center gap-2">
                <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-danger-100/60 text-danger-500 text-xs rounded-full border border-danger-500/30"
                    title={probe.error || t('probe.unreachableTitle')}
                >
                    <span className="size-1.5 rounded-full bg-danger-500" />
                    {t('probe.unreachable', { rtt: probe.rttMs })}
                </span>
                {refreshBtn}
            </div>
        );
    }

    // PBX 通了 — 補充分機註冊細節
    let extInfo = '';
    let badgeClass = 'bg-success-100/60 text-success-500 border-success-500/30';
    let dotClass = 'bg-success-500';
    let checkmark = '✓';
    if (total === 0) {
        extInfo = t('probe.extNone');
    } else if (registeredCount === total) {
        extInfo = t('probe.extRegistered', { registered: registeredCount, total });
    } else if (registeredCount === 0) {
        extInfo = t('probe.extNoneRegistered', { total });
        badgeClass = 'bg-warn-100 text-warn-500 border-warn-500/30';
        dotClass = 'bg-warn-500';
        checkmark = '⚠';
    } else {
        extInfo = t('probe.extRegistered', { registered: registeredCount, total });
        badgeClass = 'bg-warn-100 text-warn-500 border-warn-500/30';
        dotClass = 'bg-warn-500';
        checkmark = '⚠';
    }

    return (
        <div className="flex items-center gap-2">
            <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${badgeClass} text-xs rounded-full border`}
                title={`${probe.response || 'reachable'} · RTT ${probe.rttMs}ms`}
            >
                <span className={`size-1.5 rounded-full ${dotClass}`} />
                {checkmark} {t('probe.reachable', { status: probe.reachable ? t('probe.connected') : t('probe.disconnected'), rtt: probe.rttMs })}{extInfo}
            </span>
            {refreshBtn}
        </div>
    );
}

// ════════════════════════════════════════════════════════════
// Section save bar — 每個 Card 底部一條「dirty 提示 + 儲存按鈕」
// ════════════════════════════════════════════════════════════
function SectionSaveBar({ dirty, status, onSave, applyHint }) {
    const { t } = useTranslation('tenant');
    const saving = status?.saving;
    const justSavedOk = !dirty && !saving && status?.savedAt && status?.applied !== false;
    const justSavedNotReady = !dirty && !saving && status?.savedAt && status?.applied === false;
    return (
        <div className="mt-4 pt-3 border-t border-cream-100 flex items-center gap-3">
            <div className="flex-1 text-xs">
                {dirty && <span className="text-warn-500">{t('saveBar.dirty')}</span>}
                {justSavedOk && (
                    <span className="text-success-500">
                        {applyHint
                            ? t('saveBar.savedWithHint', { hint: applyHint, time: status.savedAt.toLocaleTimeString() })
                            : t('saveBar.saved', { time: status.savedAt.toLocaleTimeString() })}
                    </span>
                )}
                {justSavedNotReady && (
                    <span className="text-warn-500">
                        {t('saveBar.savedNotReady')}
                    </span>
                )}
                {status?.error && (
                    <span className="text-danger-500">{t('saveBar.saveFailed', { msg: status.error.message })}</span>
                )}
            </div>
            <Button
                onClick={onSave}
                disabled={!dirty || saving}
                variant={dirty ? 'accent' : 'ghost'}
                size="sm"
            >
                <Save size={12} /> {saving ? t('saveBar.saving') : t('common:actions.save')}
            </Button>
        </div>
    );
}

// ════════════════════════════════════════════════════════════
// DID mapping table — trunk code (748) → avatar dropdown
// 代表號 / 友善名稱可選填，avatarId 必填
// ════════════════════════════════════════════════════════════
function DidMappingTable({ hostId, outboundPrefixes, internalExts = [], trunkMode = false, trunkDids = [] }) {
    const { t } = useTranslation('tenant');
    const [dids, setDids] = useState([]);
    const [avatars, setAvatars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);   // phoneNumber being saved/deleted
    const [justSaved, setJustSaved] = useState(null);  // phoneNumber of newly saved row (for highlight + scroll)
    const [pendingTrunks, setPendingTrunks] = useState([]);   // 用戶手動加的 trunk code（還沒存 sipPhoneNumbers）
    const [newTrunkInput, setNewTrunkInput] = useState('');

    async function reload() {
        setLoading(true);
        setError(null);
        try {
            const [d, a] = await Promise.all([
                didApi.listByHost(hostId),
                avatarsApi.listForTenant(hostId).catch(() => ({ avatars: [] })),
            ]);
            setDids(d.dids || []);
            setAvatars(a.avatars || []);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId]);

    async function save(phoneNumber, patch) {
        setBusy(phoneNumber);
        // 鎖定 row 在視窗的相對位置 — 不論 reload 後 row 跑到哪都對齊
        // （reload 會把該 row 從「尚未綁定」section 移到「已綁定」上面，DOM 大幅 reshuffle）
        const beforeRow = document.querySelector(`[data-trunk-row="${phoneNumber}"]`);
        const beforeTop = beforeRow?.getBoundingClientRect()?.top ?? null;
        try {
            await didApi.upsert(hostId, phoneNumber, patch);
            await reload();
            setJustSaved(phoneNumber);
            // 等 React render → 找 row 新位置 → scrollBy 把它調回原視窗位置
            requestAnimationFrame(() => {
                const afterRow = document.querySelector(`[data-trunk-row="${phoneNumber}"]`);
                if (afterRow && beforeTop !== null) {
                    const afterTop = afterRow.getBoundingClientRect().top;
                    const delta = afterTop - beforeTop;
                    if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: 'instant' });
                }
            });
            setTimeout(() => setJustSaved(null), 1500);
        } catch (e) {
            alert(t('saveBar.saveFailed', { msg: e.message }));
        } finally {
            setBusy(null);
        }
    }

    async function remove(phoneNumber) {
        if (!confirm(t('didTable.removeConfirm', { trunk: phoneNumber }))) return;
        setBusy(phoneNumber);
        try {
            await didApi.remove(hostId, phoneNumber);
            await reload();
        } catch (e) {
            alert(t('didTable.deleteFailed', { msg: e.message }));
        } finally {
            setBusy(null);
        }
    }

    // 排除「內線 ext」的 doc（那些在「內線路由」section 管理；避免兩邊都顯示）
    const internalSet = new Set(internalExts.map(String));
    const externalDids = dids.filter((d) => !internalSet.has(String(d.phoneNumber)));
    // 「尚未綁定的 trunk」= 用戶手動加的 pendingTrunks（外播 prefix 已跟進線解耦，不再從 outboundPrefixes 推導）
    // trunk 模式：Trunk 設定卡填的 dids[] 自動當候選 —— 填完進線號碼馬上能在這綁 avatar，不用重打
    const existingKeys = new Set(externalDids.map((d) => d.phoneNumber));
    const seededDids = trunkMode ? trunkDids.filter((d) => !existingKeys.has(d)) : [];
    const seededSet = new Set(seededDids);
    const suggestedNew = [...seededDids, ...pendingTrunks.filter((p) => !existingKeys.has(p) && !seededSet.has(p))];
    const suggestedSet = new Set(suggestedNew);   // dedup

    function addPendingTrunk() {
        const code = newTrunkInput.trim();
        if (!code) return;
        if (existingKeys.has(code) || suggestedSet.has(code)) {
            alert(t('didTable.trunkExists', { trunk: code }));
            return;
        }
        setPendingTrunks((arr) => [...arr, code]);
        setNewTrunkInput('');
    }

    // 存上去後從 pendingTrunks 移除（dids 已含這筆）
    useEffect(() => {
        setPendingTrunks((arr) => arr.filter((t) => !existingKeys.has(t)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dids]);

    if (loading) return <div className="text-sm text-ink-400 italic">{t('common:state.loading')}</div>;
    if (error) return <ErrorAlert error={error} onRetry={reload} />;

    return (
        <div className="space-y-3">
            {/* 新增 trunk 入口 — 置頂 */}
            <div className="flex items-center gap-2 pb-3 border-b border-cream-200">
                <input
                    type="text"
                    value={newTrunkInput}
                    onChange={(e) => setNewTrunkInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); addPendingTrunk(); }
                    }}
                    placeholder={t('didTable.newTrunkPlaceholder')}
                    className="flex-1 px-3 py-2 bg-white border border-cream-300 rounded-md font-mono text-sm focus:outline-none focus:border-accent-500"
                />
                <Button onClick={addPendingTrunk} disabled={!newTrunkInput.trim()} variant="accent" size="sm">
                    <Plus size={12} /> {t('didTable.addTrunk')}
                </Button>
            </div>

            {externalDids.length === 0 && suggestedNew.length === 0 && (
                <div className="text-sm text-ink-400 italic">
                    {t('didTable.empty')}
                </div>
            )}

            {externalDids.map((d) => (
                <DidRow
                    key={d.phoneNumber}
                    did={d}
                    avatars={avatars}
                    trunkMode={trunkMode}
                    busy={busy === d.phoneNumber}
                    justSaved={justSaved === d.phoneNumber}
                    onSave={(patch) => save(d.phoneNumber, patch)}
                    onRemove={() => remove(d.phoneNumber)}
                />
            ))}

            {suggestedNew.length > 0 && (
                <div className="pt-2 border-t border-cream-200">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">
                        {t('didTable.newTrunkSection')}
                    </div>
                    <div className="space-y-2">
                        {suggestedNew.map((trunk) => (
                            <DidRow
                                key={trunk}
                                did={{ phoneNumber: trunk, avatarId: '', representativeNumber: '', friendlyName: '', defaultLanguage: 'zh-TW', enabled: true }}
                                avatars={avatars}
                                trunkMode={trunkMode}
                                isNew
                                busy={busy === trunk}
                                justSaved={justSaved === trunk}
                                onSave={(patch) => save(trunk, patch)}
                                onRemove={() => setPendingTrunks((arr) => arr.filter((t) => t !== trunk))}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════
// 內線路由 table — 我們的 ext (327 等) → avatar dropdown
// 內線分機（PBX 端任意 ext）轉接到「我們其中一支已註冊的 ext」進來時，
// backend resolveAvatarId 找不到 did 會 fallback 查 channelId 的 -ext-N
// → 對應到 sipPhoneNumbers/{hostId}/{我們的ext} 找 avatar。
// 這張表就是建立/維護那批 doc（key = 我們的 ext）。
// ════════════════════════════════════════════════════════════
function InternalExtMappingTable({ hostId, ipPhones }) {
    const { t } = useTranslation('tenant');
    const [dids, setDids] = useState([]);
    const [avatars, setAvatars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);
    const [justSaved, setJustSaved] = useState(null);

    async function reload() {
        setLoading(true);
        setError(null);
        try {
            const [d, a] = await Promise.all([
                didApi.listByHost(hostId),
                avatarsApi.listForTenant(hostId).catch(() => ({ avatars: [] })),
            ]);
            setDids(d.dids || []);
            setAvatars(a.avatars || []);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId]);

    async function save(ext, patch) {
        setBusy(ext);
        try {
            await didApi.upsert(hostId, ext, patch);
            await reload();
            setJustSaved(ext);
            setTimeout(() => setJustSaved(null), 1500);
        } catch (e) {
            alert(t('saveBar.saveFailed', { msg: e.message }));
        } finally {
            setBusy(null);
        }
    }

    async function remove(ext) {
        if (!confirm(t('internalTable.removeConfirm', { ext }))) return;
        setBusy(ext);
        try {
            await didApi.remove(hostId, ext);
            await reload();
        } catch (e) {
            alert(t('didTable.deleteFailed', { msg: e.message }));
        } finally {
            setBusy(null);
        }
    }

    if (loading) return <div className="text-sm text-ink-400 italic">{t('common:state.loading')}</div>;
    if (error) return <ErrorAlert error={error} onRetry={reload} />;
    if (!ipPhones || ipPhones.length === 0) {
        return <div className="text-sm text-ink-400 italic">{t('internalTable.noPhones')}</div>;
    }

    // 每支 ipPhone ext 一行，合併既有 did doc
    const didByExt = new Map(dids.map((d) => [String(d.phoneNumber), d]));
    return (
        <div className="space-y-3">
            {ipPhones.map((p) => {
                const ext = String(p.ext);
                const did = didByExt.get(ext);
                return (
                    <InternalExtRow
                        key={ext}
                        ext={ext}
                        did={did || { phoneNumber: ext, avatarId: '', friendlyName: '', defaultLanguage: 'zh-TW', enabled: true }}
                        avatars={avatars}
                        busy={busy === ext}
                        justSaved={justSaved === ext}
                        onSave={(patch) => save(ext, patch)}
                        onRemove={didByExt.has(ext) ? (() => remove(ext)) : null}
                    />
                );
            })}
        </div>
    );
}

function InternalExtRow({ ext, did, avatars, busy, justSaved, onSave, onRemove }) {
    const { t } = useTranslation('tenant');
    const [draft, setDraft] = useState({
        avatarId: did.avatarId || '',
        friendlyName: did.friendlyName || '',
        defaultLanguage: did.defaultLanguage || 'zh-TW',
    });
    const [dirty, setDirty] = useState(false);
    function update(patch) { setDraft((d) => ({ ...d, ...patch })); setDirty(true); }
    const canSave = dirty;
    return (
        <div className={[
            'grid grid-cols-12 gap-2 items-start rounded-md p-3 border transition-colors duration-700',
            justSaved ? 'bg-accent-50 border-accent-400 ring-2 ring-accent-300/40' : 'bg-white border-cream-200',
        ].join(' ')}>
            <div className="col-span-2">
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1">{t('internalTable.extColumn')}</div>
                <div className="font-mono text-sm font-medium text-ink-900 px-2 py-1.5 bg-cream-100 rounded">{ext}</div>
            </div>
            <div className="col-span-4">
                <Input
                    label={t('row.label.optional')}
                    placeholder={t('internalTable.labelPlaceholder')}
                    value={draft.friendlyName}
                    onChange={(e) => update({ friendlyName: e.target.value })}
                />
            </div>
            <div className="col-span-5">
                <label className="block text-[10px] uppercase tracking-wider font-mono text-ink-400 mb-1">{t('row.aiAgent')}</label>
                <select
                    value={draft.avatarId}
                    onChange={(e) => update({ avatarId: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm bg-white border border-cream-200 rounded focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                >
                    <option value="">{t('internalTable.unassigned')}</option>
                    {avatars.map((a) => (
                        <option key={a.id} value={a.id}>{a.name || a.id.slice(0, 8)}</option>
                    ))}
                </select>
            </div>
            <div className="col-span-1 flex flex-col gap-1 pt-5 items-end">
                <Button size="sm" variant={canSave ? 'primary' : 'ghost'} disabled={!canSave || busy} onClick={() => onSave(draft)}>
                    {busy ? '...' : t('row.bind')}
                </Button>
                {onRemove && (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={onRemove}>
                        <Trash2 size={12} className="text-danger-500" />
                    </Button>
                )}
            </div>
        </div>
    );
}

// 可折疊 Card：標題列點擊展開/收合。描述永遠可見方便瀏覽
function CollapsibleCard({ title, description, headerExtra, defaultOpen = false, summary, children, className = '' }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Card className={className}>
            <div className="flex items-start justify-between gap-3">
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="flex-1 flex items-start gap-2 text-left group min-w-0"
                    aria-expanded={open}
                >
                    <span className="mt-0.5 shrink-0 text-ink-400 group-hover:text-ink-700 transition">
                        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <h2 className="heading-display text-lg font-semibold text-ink-900">{title}</h2>
                            {!open && summary && (
                                <span className="text-xs text-ink-500 truncate">{summary}</span>
                            )}
                        </div>
                        {description && <p className="text-xs text-ink-400 mt-0.5">{description}</p>}
                    </div>
                </button>
                {headerExtra && <div className="shrink-0">{headerExtra}</div>}
            </div>
            {open && <div className="mt-4">{children}</div>}
        </Card>
    );
}

function DidRow({ did, avatars, busy, isNew = false, justSaved = false, trunkMode = false, onSave, onRemove }) {
    const { t } = useTranslation('tenant');
    const [draft, setDraft] = useState({
        avatarId: did.avatarId || '',
        representativeNumber: did.representativeNumber || '',
        friendlyName: did.friendlyName || '',
        defaultLanguage: did.defaultLanguage || 'zh-TW',
        // SIP Trunk：席次型別綁在 DID 上（分機模式綁在 ipPhones，用不到這欄）
        ...(trunkMode ? { seatType: did.seatType || '' } : {}),
    });
    const [dirty, setDirty] = useState(false);
    function update(patch) {
        setDraft((d) => ({ ...d, ...patch }));
        setDirty(true);
    }
    // avatar 選填 — 之後可再編輯。新 row 任意填欄位就能存；既有 row 改了任何欄位就能存
    const canSave = dirty || isNew;
    return (
        <div
            data-trunk-row={did.phoneNumber}
            className={[
                'grid grid-cols-12 gap-2 items-start rounded-md p-3 border transition-colors duration-700',
                justSaved
                    ? 'bg-accent-50 border-accent-400 ring-2 ring-accent-300/40'
                    : 'bg-white border-cream-200',
            ].join(' ')}
        >
            {/* trunk code */}
            <div className="col-span-2">
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1">
                    trunk
                </div>
                <div className="font-mono text-sm font-medium text-ink-900 px-2 py-1.5 bg-cream-100 rounded">
                    {did.phoneNumber}
                </div>
                {trunkMode && (
                    <select
                        value={draft.seatType || ''}
                        onChange={(e) => update({ seatType: e.target.value })}
                        className="mt-1.5 w-full px-1.5 py-1 bg-white border border-cream-300 rounded text-xs focus:outline-none focus:border-accent-500"
                        title={t('trunk.didSeat.title')}
                    >
                        <option value="">{t('trunk.didSeat.auto')}</option>
                        <option value="plan_01">{t('trunk.didSeat.plan01')}</option>
                        <option value="plan_02">{t('trunk.didSeat.plan02')}</option>
                    </select>
                )}
            </div>

            {/* representativeNumber */}
            <div className="col-span-3">
                <Input
                    label={t('didTable.repNumber')}
                    placeholder="0277027154"
                    value={draft.representativeNumber}
                    onChange={(e) => update({ representativeNumber: e.target.value })}
                />
            </div>

            {/* friendlyName */}
            <div className="col-span-3">
                <Input
                    label={t('row.label.optional')}
                    placeholder={t('didTable.labelPlaceholder')}
                    value={draft.friendlyName}
                    onChange={(e) => update({ friendlyName: e.target.value })}
                />
            </div>

            {/* avatar dropdown — 必填 */}
            <div className="col-span-3">
                <label className="block text-[10px] uppercase tracking-wider font-mono text-ink-400 mb-1">
                    {t('row.aiAgentOptional')}
                </label>
                <select
                    value={draft.avatarId}
                    onChange={(e) => update({ avatarId: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm bg-white border border-cream-200 rounded focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                >
                    <option value="">{t('row.selectOne')}</option>
                    {avatars.map((a) => (
                        <option key={a.id} value={a.id}>
                            {a.name || a.id.slice(0, 8)}
                        </option>
                    ))}
                </select>
            </div>

            {/* actions */}
            <div className="col-span-1 flex flex-col gap-1 pt-5 items-end">
                <Button
                    size="sm"
                    variant={canSave ? 'primary' : 'ghost'}
                    disabled={!canSave || busy}
                    onClick={() => onSave(draft)}
                >
                    {busy ? '...' : draft.avatarId ? t('row.bind') : t('common:actions.save')}
                </Button>
                {onRemove && (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={onRemove}>
                        <Trash2 size={12} className="text-danger-500" />
                    </Button>
                )}
            </div>
        </div>
    );
}
